import { z } from 'zod';

const defaultApplicationName: string = 'Assistant';
const defaultCompanyName: string = '{Company name}';
const defaultMaxFileUploadSizeMb: number = 20;
const defaultMaxAudioFileUploadSizeMb: number = 25;
const defaultRequestTimeoutInSeconds: number = 180;
const defaultTaskResultExpiryTime: number = 3 * 24 * 60 * 60;

const FONT_SETTINGS_SCHEMA = z.object({
  family: z.string().min(1).optional(),
  size: z.number().min(1).optional(),
});

export type FontSettings = z.infer<typeof FONT_SETTINGS_SCHEMA>;

const COLOR_SETTINGS_SCHEMA = z.record(z.string().min(1), z.string().min(1));

export type ColorSettings = z.infer<typeof COLOR_SETTINGS_SCHEMA>;

const IMAGE_SCHEMA = z.object({
  data: z.string().min(1),
  type: z.enum(['image/svg+xml', 'image/png']),
  name: z.string().min(1),
});

const CUSTOMIZATION_RESPONSE_SCHEMA = z.object({
  application_name: z.string().min(1),
  company_name: z.string().min(1),
  logo: IMAGE_SCHEMA,
  favicon: IMAGE_SCHEMA,
  colors: COLOR_SETTINGS_SCHEMA,
  fonts: FONT_SETTINGS_SCHEMA,
  privacy_policy_notice: z.any(),
  accessibility_statement: z.string(),
  help_center_enabled: z.boolean(),
  imprint_link: z.url(),
  create_your_own_enabled: z.boolean(),
  support_email: z.email(),
  support_phone: z.string(),
  documentation_link: z.url(),
  help_center_link: z.url(),
  default_applications: z.array(z.string()),
});

export const CONFIG_RESPONSE_SCHEMA = z.object({
  maxFileUploadSizeMB: z.number().min(0).default(defaultMaxFileUploadSizeMb),
  maxAudioFileUploadSizeMB: z.number().min(0).default(defaultMaxAudioFileUploadSizeMb),
  requestTimeoutInSeconds: z.number().min(0).default(defaultRequestTimeoutInSeconds),
  taskResultExpiryTime: z.number().min(0).default(defaultTaskResultExpiryTime),
  osProxyUrl: z.url().nullable().default(null),
  customization: CUSTOMIZATION_RESPONSE_SCHEMA.default({
    application_name: defaultApplicationName,
    company_name: defaultCompanyName,
    logo: {
      name: 'logo.svg',
      type: 'image/svg+xml',
      data: 'PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGhlaWdodD0iNzgiIHdpZHRoPSI3OCIgdmlld0JveD0iMjQgMjQgMTAyIDEwMiI+CiAgPHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IndoaXRlIiAvPgogIDxwYXRoIGQ9Im0zNC42NyAxMDYuNDMtNS41NS0xMy41NUExOS4xNCAxOS4xNCAwIDAgMCAyOSA1Ny40Mmw1LjQ3LTEzLjU5YTMzLjc4IDMzLjc4IDAgMCAxIC4yIDYyLjZabTIyLjkzIDE3LjEyLTguNjgtMTEuOGE0NS4yOCA0NS4yOCAwIDAgMCA4LjYxLTguMjdMNjkgMTEyLjYyYTU5LjgyIDU5LjgyIDAgMCAxLTExLjQgMTAuOTNaIiAvPgogIDxwYXRoIGQ9Ik04Mi4yMyA3NS4xN2E2MC4zNyA2MC4zNyAwIDAgMC0yNC42NC00OC4zOWwtOC42OCAxMS44MWE0NS42MyA0NS42MyAwIDAgMSAxOC42OCAzNi41OCA2MC4yOCA2MC4yOCAwIDAgMCAyNC40OCA0OC4zOGw4LjY4LTExLjhhNDUuNiA0NS42IDAgMCAxLTE4LjUyLTM2LjU4Wm05Ljg1LTI4LjIzLTExLjQ3LTkuMTJhNjAuMTQgNjAuMTQgMCAwIDEgMTEuOTQtMTEuMzdsOC41NiAxMS44OGE0NS41IDQ1LjUgMCAwIDAtOS4wMyA4LjYxWk0xMTUgMTA2LjUyYTM0LjMyIDM0LjMyIDAgMCAxLS4yOS02Mi41Mmw2LjEgMTMuMzJhMTkuNjggMTkuNjggMCAwIDAgLjE5IDM1LjgzWiIgLz4KPC9zdmc+Cg==',
    },
    favicon: {
      name: 'logo.svg',
      type: 'image/svg+xml',
      data: 'PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGhlaWdodD0iNzgiIHdpZHRoPSI3OCIgdmlld0JveD0iMjQgMjQgMTAyIDEwMiI+CiAgPHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IndoaXRlIiAvPgogIDxwYXRoIGQ9Im0zNC42NyAxMDYuNDMtNS41NS0xMy41NUExOS4xNCAxOS4xNCAwIDAgMCAyOSA1Ny40Mmw1LjQ3LTEzLjU5YTMzLjc4IDMzLjc4IDAgMCAxIC4yIDYyLjZabTIyLjkzIDE3LjEyLTguNjgtMTEuOGE0NS4yOCA0NS4yOCAwIDAgMCA4LjYxLTguMjdMNjkgMTEyLjYyYTU5LjgyIDU5LjgyIDAgMCAxLTExLjQgMTAuOTNaIiAvPgogIDxwYXRoIGQ9Ik04Mi4yMyA3NS4xN2E2MC4zNyA2MC4zNyAwIDAgMC0yNC42NC00OC4zOWwtOC42OCAxMS44MWE0NS42MyA0NS42MyAwIDAgMSAxOC42OCAzNi41OCA2MC4yOCA2MC4yOCAwIDAgMCAyNC40OCA0OC4zOGw4LjY4LTExLjhhNDUuNiA0NS42IDAgMCAxLTE4LjUyLTM2LjU4Wm05Ljg1LTI4LjIzLTExLjQ3LTkuMTJhNjAuMTQgNjAuMTQgMCAwIDEgMTEuOTQtMTEuMzdsOC41NiAxMS44OGE0NS41IDQ1LjUgMCAwIDAtOS4wMyA4LjYxWk0xMTUgMTA2LjUyYTM0LjMyIDM0LjMyIDAgMCAxLS4yOS02Mi41Mmw2LjEgMTMuMzJhMTkuNjggMTkuNjggMCAwIDAgLjE5IDM1LjgzWiIgLz4KPC9zdmc+Cg==',
    },
    colors: {},
    fonts: {
      family: 'Raleway',
      size: 16,
    },
    support_email: 'a11y@aleph-alpha.com',
    support_phone: '+49 6221 352 13 14 18',
    privacy_policy_notice: {
      localizations: [
        {
          locale: 'de',
          text: 'Bitte geben Sie keine persönlichen, sensiblen oder geheimen Daten in dieses Tool ein, da es sich um ein Testsystem handelt. Weitere Informationen darüber, wie wir personenbezogene Daten verarbeiten, finden Sie in unserer',
          link_url: 'https://aleph-alpha.com/de/impressum/',
          link_title: 'Datenschutzrichtlinie',
        },
        {
          locale: 'en',
          text: 'Please do not enter any personal, sensitive or classified data in this tool, as it is a test system. You can find more information on how we process personal data in our',
          link_url: 'https://aleph-alpha.com/de/impressum/',
          link_title: 'Privacy Policy',
        },
      ],
    },
    documentation_link: 'https://docs.aleph-alpha.com/products/pharia-ai/pharia-assistant/',
    help_center_link: 'https://supportportal.aleph-alpha.com',
    help_center_enabled: true,
    imprint_link: 'https://aleph-alpha.com/de/impressum/',
    accessibility_statement:
      "We are currently working to improve our platform's accessibility to meet the necessary standards. After successfully addressing the issues identified in our previous audit, we will publish a comprehensive Accessibility Statement. Thank you for your understanding as we strive to create a more inclusive experience for everyone.",
    create_your_own_enabled: true,
    default_applications: [],
  }),
  hasError: z.boolean().optional(),
});

export type ConfigResponse = z.infer<typeof CONFIG_RESPONSE_SCHEMA>;

export const DEFAULT_CONFIG: ConfigResponse = {
  maxFileUploadSizeMB: defaultMaxFileUploadSizeMb,
  maxAudioFileUploadSizeMB: defaultMaxAudioFileUploadSizeMb,
  requestTimeoutInSeconds: defaultRequestTimeoutInSeconds,
  taskResultExpiryTime: defaultTaskResultExpiryTime,
  osProxyUrl: null,
  customization: {
    application_name: defaultApplicationName,
    company_name: defaultCompanyName,
    logo: {
      name: 'logo.svg',
      type: 'image/svg+xml',
      data: 'PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGhlaWdodD0iNzgiIHdpZHRoPSI3OCIgdmlld0JveD0iMjQgMjQgMTAyIDEwMiI+CiAgPHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IndoaXRlIiAvPgogIDxwYXRoIGQ9Im0zNC42NyAxMDYuNDMtNS41NS0xMy41NUExOS4xNCAxOS4xNCAwIDAgMCAyOSA1Ny40Mmw1LjQ3LTEzLjU5YTMzLjc4IDMzLjc4IDAgMCAxIC4yIDYyLjZabTIyLjkzIDE3LjEyLTguNjgtMTEuOGE0NS4yOCA0NS4yOCAwIDAgMCA4LjYxLTguMjdMNjkgMTEyLjYyYTU5LjgyIDU5LjgyIDAgMCAxLTExLjQgMTAuOTNaIiAvPgogIDxwYXRoIGQ9Ik04Mi4yMyA3NS4xN2E2MC4zNyA2MC4zNyAwIDAgMC0yNC42NC00OC4zOWwtOC42OCAxMS44MWE0NS42MyA0NS42MyAwIDAgMSAxOC42OCAzNi41OCA2MC4yOCA2MC4yOCAwIDAgMCAyNC40OCA0OC4zOGw4LjY4LTExLjhhNDUuNiA0NS42IDAgMCAxLTE4LjUyLTM2LjU4Wm05Ljg1LTI4LjIzLTExLjQ3LTkuMTJhNjAuMTQgNjAuMTQgMCAwIDEgMTEuOTQtMTEuMzdsOC41NiAxMS44OGE0NS41IDQ1LjUgMCAwIDAtOS4wMyA4LjYxWk0xMTUgMTA2LjUyYTM0LjMyIDM0LjMyIDAgMCAxLS4yOS02Mi41Mmw2LjEgMTMuMzJhMTkuNjggMTkuNjggMCAwIDAgLjE5IDM1LjgzWiIgLz4KPC9zdmc+Cg==',
    },
    favicon: {
      name: 'logo.svg',
      type: 'image/svg+xml',
      data: 'PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGhlaWdodD0iNzgiIHdpZHRoPSI3OCIgdmlld0JveD0iMjQgMjQgMTAyIDEwMiI+CiAgPHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IndoaXRlIiAvPgogIDxwYXRoIGQ9Im0zNC42NyAxMDYuNDMtNS41NS0xMy41NUExOS4xNCAxOS4xNCAwIDAgMCAyOSA1Ny40Mmw1LjQ3LTEzLjU5YTMzLjc4IDMzLjc4IDAgMCAxIC4yIDYyLjZabTIyLjkzIDE3LjEyLTguNjgtMTEuOGE0NS4yOCA0NS4yOCAwIDAgMCA4LjYxLTguMjdMNjkgMTEyLjYyYTU5LjgyIDU5LjgyIDAgMCAxLTExLjQgMTAuOTNaIiAvPgogIDxwYXRoIGQ9Ik04Mi4yMyA3NS4xN2E2MC4zNyA2MC4zNyAwIDAgMC0yNC42NC00OC4zOWwtOC42OCAxMS44MWE0NS42MyA0NS42MyAwIDAgMSAxOC42OCAzNi41OCA2MC4yOCA2MC4yOCAwIDAgMCAyNC40OCA0OC4zOGw4LjY4LTExLjhhNDUuNiA0NS42IDAgMCAxLTE4LjUyLTM2LjU4Wm05Ljg1LTI4LjIzLTExLjQ3LTkuMTJhNjAuMTQgNjAuMTQgMCAwIDEgMTEuOTQtMTEuMzdsOC41NiAxMS44OGE0NS41IDQ1LjUgMCAwIDAtOS4wMyA4LjYxWk0xMTUgMTA2LjUyYTM0LjMyIDM0LjMyIDAgMCAxLS4yOS02Mi41Mmw2LjEgMTMuMzJhMTkuNjggMTkuNjggMCAwIDAgLjE5IDM1LjgzWiIgLz4KPC9zdmc+Cg==',
    },
    colors: {},
    fonts: {
      family: 'Raleway',
      size: 16,
    },
    privacy_policy_notice: {},
    create_your_own_enabled: true,
    default_applications: [],
    accessibility_statement: '',
    help_center_enabled: false,
    imprint_link: '',
    support_email: '',
    support_phone: '',
    documentation_link: '',
    help_center_link: '',
  },
  hasError: false,
} as const;
